import { useEffect, useState } from 'react';
import { AUTOSAVE_SLOT, listStateSummaries, type StateRecord } from '../storage/saveRepository';
import type { RomMeta } from '../storage/romRepository';
import { CheatManager } from './CheatManager';
import { PAD_SPEED_MAX, PAD_SPEED_MIN, PAD_SPEED_STEP } from './VirtualPad';

export interface PauseMenuProps {
  rom: RomMeta;
  padVisible: boolean;
  onResume: () => void;
  onExit: () => void;
  onReset: () => void;
  onSaveState: (slot: number) => Promise<void>;
  onLoadState: (slot: number) => Promise<void>;
  onTogglePad: () => void;
  speed: number;
  onSpeed: (mult: number) => void;
  onScreenshot: () => Promise<void>;
  /** チートの内容が変わったときにセッションへ反映する */
  onCheatsChanged: () => void;
  cheatsSupported: boolean;
  /** バーチャルパッドの配置編集へ入る */
  onEditPad: () => void;
  /** マルチディスク（2 枚以上のときだけ渡す） */
  discInfo?: { count: number; index: number; labels: string[] } | null;
  onSwapDisc?: (index: number) => Promise<void>;
}

type Summary = Omit<StateRecord, 'data'> & { size: number };

/** 手動スロットは 0–9（表示は 1–10）。取り込み・同期と同じ上限に揃える。 */
const MANUAL_SLOTS = 10;

export function PauseMenu(props: PauseMenuProps) {
  const { rom, onResume, onExit, onReset, onSaveState, onLoadState, onTogglePad, padVisible, speed, onSpeed } =
    props;
  const [states, setStates] = useState<Summary[]>([]);
  const [tab, setTab] = useState<'main' | 'states' | 'cheats'>('main');
  const [swapping, setSwapping] = useState(false);

  const reload = () => void listStateSummaries(rom.id).then(setStates);
  useEffect(reload, [rom.id]);
  // 同期直後やタブ切替で最新のスロットを拾う
  useEffect(() => {
    if (tab === 'states') reload();
  }, [tab, rom.id]);

  const slotOf = (slot: number) => states.find((s) => s.slot === slot);
  const discs = props.discInfo && props.discInfo.count > 1 ? props.discInfo : null;

  return (
    <div className="absolute inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center">
      <div className="animate-fade-in flex w-full max-w-md max-h-[min(88dvh,var(--app-height,100dvh))] flex-col overflow-hidden rounded-t-3xl border-t border-base-600 bg-base-800 sm:max-h-[min(90dvh,720px)] sm:rounded-3xl sm:border">
        <div className="shrink-0 p-4 pb-0">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{rom.title}</p>
              <p className="text-[0.65rem] text-slate-500">一時停止中</p>
            </div>
            <button type="button" className="btn-primary !min-h-0 !px-4 !py-2" onClick={onResume}>
              再開
            </button>
          </div>
        </div>

        <div className="shrink-0 px-4 pb-3 pt-3">
          <div className="flex gap-1 rounded-xl bg-base-900 p-1">
            <TabButton active={tab === 'main'} onClick={() => setTab('main')}>
              操作
            </TabButton>
            <TabButton active={tab === 'states'} onClick={() => setTab('states')}>
              セーブ
            </TabButton>
            <TabButton active={tab === 'cheats'} onClick={() => setTab('cheats')}>
              チート
            </TabButton>
          </div>
        </div>

        <div className="scroll-panel min-h-0 flex-1 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {tab === 'cheats' ? (
            <CheatManager
              romId={rom.id}
              system={rom.system}
              onChange={props.onCheatsChanged}
              unsupported={!props.cheatsSupported}
            />
          ) : tab === 'main' ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <button type="button" className="btn-ghost" onClick={onReset}>
                  リセット
                </button>
                <button type="button" className="btn-ghost" onClick={onTogglePad}>
                  パッド {padVisible ? '非表示' : '表示'}
                </button>
                <button type="button" className="btn-ghost" onClick={() => void props.onScreenshot()}>
                  スクリーンショット
                </button>
                <button type="button" className="btn-ghost" onClick={props.onEditPad}>
                  パッド配置を編集
                </button>
                <button type="button" className="btn-danger" onClick={onExit}>
                  終了
                </button>
              </div>

              <div>
                <label className="field-label" htmlFor="speed">
                  速度 ({speed.toFixed(1)}x)
                </label>
                <input
                  id="speed"
                  type="range"
                  min={PAD_SPEED_MIN}
                  max={PAD_SPEED_MAX}
                  step={PAD_SPEED_STEP}
                  value={speed}
                  onChange={(e) => {
                    onSpeed(Number(e.target.value));
                  }}
                  className="w-full accent-accent"
                />
              </div>

              {discs && props.onSwapDisc && (
                <div>
                  <p className="field-label">ディスク交換</p>
                  <p className="mb-2 text-[0.65rem] text-slate-500">
                    ゲームがディスク交換を求めたら、次のディスクを選んでください
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {discs.labels.map((label, i) => {
                      const active = i === discs.index;
                      return (
                        <button
                          key={`${label}-${i}`}
                          type="button"
                          disabled={active || swapping}
                          className={active ? 'btn-primary !opacity-100' : 'btn-ghost'}
                          onClick={() => {
                            if (!props.onSwapDisc || active) return;
                            setSwapping(true);
                            void props
                              .onSwapDisc(i)
                              .catch(() => undefined)
                              .finally(() => setSwapping(false));
                          }}
                        >
                          {active ? `${label}（挿入中）` : label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {states.length === 0 && (
                <p className="rounded-xl border border-dashed border-base-600 px-3 py-2 text-[0.7rem] text-slate-500">
                  まだ手動ステートがありません。ここで保存するか、ライブラリの「セーブデータを読み込む」でファイルを取り込んでください。サーバー同期だけでは、サーバにステートが無いとスロットは埋まりません。
                </p>
              )}
              <SlotRow
                label="オートセーブ"
                summary={slotOf(AUTOSAVE_SLOT)}
                onLoad={() => void onLoadState(AUTOSAVE_SLOT)}
              />
              {Array.from({ length: MANUAL_SLOTS }, (_, i) => (
                <SlotRow
                  key={i}
                  label={`スロット ${i + 1}`}
                  summary={slotOf(i)}
                  onSave={async () => {
                    await onSaveState(i);
                    reload();
                  }}
                  onLoad={() => void onLoadState(i)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-lg py-2 text-xs font-medium transition-colors ${
        active ? 'bg-base-600 text-white' : 'text-slate-400'
      }`}
    >
      {children}
    </button>
  );
}

function SlotRow({
  label,
  summary,
  onSave,
  onLoad,
}: {
  label: string;
  summary?: Summary | undefined;
  onSave?: () => Promise<void> | void;
  onLoad: () => void;
}) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-base-600 bg-base-900 p-2">
      <div className="h-10 w-14 shrink-0 overflow-hidden rounded bg-black">
        {summary?.thumbnail && (
          <img src={summary.thumbnail} alt="" className="h-full w-full object-cover" style={{ imageRendering: 'pixelated' }} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium">{label}</p>
        <p className="truncate text-[0.65rem] text-slate-500">
          {summary ? new Date(summary.createdAt).toLocaleString('ja-JP') : '空き'}
        </p>
      </div>
      {onSave && (
        <button type="button" className="btn-ghost !min-h-0 !px-3 !py-1.5 !text-xs" onClick={() => void onSave()}>
          保存
        </button>
      )}
      <button
        type="button"
        className="btn-ghost !min-h-0 !px-3 !py-1.5 !text-xs"
        onClick={onLoad}
        disabled={!summary}
      >
        読込
      </button>
    </div>
  );
}
